import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'
import { getApprovedBusiness, getOnboardingBusiness } from '../businesses'

function isSyntheticPhone(phone?: string | null): boolean {
  if (!phone) return true
  return phone.startsWith('+firebase-') || phone.startsWith('+placeholder-')
}

function isPlaceholderEmail(email?: string | null): boolean {
  if (!email) return true
  return email.endsWith('@firebase.local')
}

export function BusinessGuard({ children }: { children?: React.ReactNode }) {
  const { vendor } = useAuth()

  if (!vendor) return null

  // Owner registration is required when ANY of: name missing, synthetic phone,
  // placeholder email (e.g. phone-only signup gets a +firebase-… phone OR a
  // *@firebase.local email), or backend says profileComplete=false.
  // Mobile parity with VendorStatus unverified / verifiedNoBusiness gate.
  const hasName = Boolean(vendor.fullName?.trim())
  if (
    !vendor.profileComplete ||
    !hasName ||
    isSyntheticPhone(vendor.phone) ||
    isPlaceholderEmail(vendor.email)
  ) {
    return <Navigate to="/onboarding/owner" replace />
  }

  const approvedBusiness = getApprovedBusiness(vendor)
  const onboardingBusiness = getOnboardingBusiness(vendor)

  // No business registered yet → go to onboarding
  if (!onboardingBusiness) {
    return <Navigate to="/onboarding" replace />
  }

  if (approvedBusiness) {
    return children ? <>{children}</> : <Outlet />
  }

  // Business exists but not approved
  if (
    onboardingBusiness.status === 'PENDING_APPROVAL' ||
    onboardingBusiness.status === 'REJECTED' ||
    onboardingBusiness.status === 'SUSPENDED' ||
    onboardingBusiness.status === 'NEEDS_RESUBMISSION'
  ) {
    return <Navigate to="/onboarding/pending" replace />
  }

  return children ? <>{children}</> : <Outlet />
}
